/**
 * StatsCard Component
 * 
 * Reusable KPI display card with icon, value, and optional trend.
 */

import React, { ReactNode } from 'react';
import { motion } from 'framer-motion';

export type StatsCardColor = 'primary' | 'success' | 'warning' | 'danger' | 'neutral';
export type StatsCardTrend = 'up' | 'down' | 'neutral';

export interface StatsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: ReactNode;
  trend?: StatsCardTrend;
  trendValue?: string;
  color?: StatsCardColor;
  className?: string;
}

const colorClasses: Record<StatsCardColor, { bg: string; icon: string; text: string }> = {
  primary: { bg: 'bg-rose-50', icon: 'text-[#c00035]', text: 'text-[#c00035]' },
  success: { bg: 'bg-green-50', icon: 'text-green-600', text: 'text-green-600' },
  warning: { bg: 'bg-amber-50', icon: 'text-amber-600', text: 'text-amber-600' },
  danger: { bg: 'bg-red-50', icon: 'text-[#c00035]', text: 'text-[#c00035]' },
  neutral: { bg: 'bg-gray-50', icon: 'text-gray-600', text: 'text-gray-600' },
};

const trendIcons: Record<StatsCardTrend, ReactNode> = {
  up: (
    <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
    </svg>
  ),
  down: (
    <svg className="w-4 h-4 text-[#c00035]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
    </svg>
  ),
  neutral: (
    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
    </svg>
  ),
};

export const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  trendValue,
  color = 'primary',
  className = '',
}) => {
  const colors = colorClasses[color];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`
        bg-white rounded-xl border border-gray-200 shadow-sm p-5
        hover:shadow-md transition-shadow
        ${className}
      `}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
          <p className={`text-2xl font-bold ${colors.text}`}>{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
          )}
        </div>

        {icon && (
          <div className={`p-3 rounded-lg ${colors.bg}`}>
            <div className={colors.icon}>{icon}</div>
          </div>
        )}
      </div>

      {trend && (
        <div className="flex items-center gap-1 mt-3 pt-3 border-t border-gray-100">
          {trendIcons[trend]}
          <span className={`text-sm ${trend === 'up' ? 'text-green-600' :
            trend === 'down' ? 'text-[#c00035]' : 'text-gray-500'
            }`}>
            {trendValue}
          </span>
        </div>
      )}
    </motion.div>
  );
};

export default StatsCard;

